// แถบซ้ายของหน้า My Job — รายชื่อระบบในเครือ ชุดเดียวกับเมนู 3 ขีด (Global Menu)
//
// อ่าน snapshot สาธารณะตัวเดียวกับที่ `/global-menu.js` ของทุกระบบอ่าน แถบนี้กับเมนู
// 3 ขีดจึงแสดงชุดเดียวกันเสมอ · เพิ่ม/ปิดระบบ และจัดกลุ่มที่ Setting → Global Menu
import { useQuery } from "@tanstack/react-query";
import {
  fetchMenuSnapshot,
  gmKeys,
  type GlobalMenuSnapshotItem,
} from "@features/global-menu/api/global-menu-api";
import { useSidebarStore } from "@features/navigation/model/sidebar-store";
import { IconChevronDown, IconCollapse, IconGrid } from "@shared/ui/icons";
import { cn } from "@shared/lib/utils";

/** ระบบที่กำลังเปิดอยู่ — เทียบ host เหมือนที่สคริปต์กลางทำ (ดู global-menu-script.ts) */
function isHere(url: string): boolean {
  try {
    return new URL(url, location.href).host === location.host;
  } catch {
    return false;
  }
}

type Section =
  | { kind: "item"; item: GlobalMenuSnapshotItem }
  | { kind: "group"; name: string; items: GlobalMenuSnapshotItem[] };

/**
 * แบ่งรายการเป็นกลุ่มตามชื่อกลุ่มที่ติดมากับแต่ละเมนู
 *
 * ฝั่ง server เรียงสมาชิกของกลุ่มเดียวกันมาติดกันให้แล้ว ที่นี่จึงแค่ไล่จากบนลงล่าง
 * แล้วขึ้นหัวข้อใหม่เมื่อชื่อกลุ่มเปลี่ยน — กติกาเดียวกับสคริปต์กลาง แถบนี้กับเมนู
 * 3 ขีดจึงจัดกลุ่มออกมาเหมือนกันเสมอ ไม่ใช่ต่างคนต่างตีความ
 */
function toSections(items: GlobalMenuSnapshotItem[]): Section[] {
  const out: Section[] = [];
  for (const item of items) {
    const name = item.group || "";
    if (!name) {
      out.push({ kind: "item", item });
      continue;
    }
    const last = out[out.length - 1];
    if (last?.kind === "group" && last.name === name) last.items.push(item);
    else out.push({ kind: "group", name, items: [item] });
  }
  return out;
}

/** คีย์จำสถานะกาง/หุบ — ใช้ที่เก็บเดียวกับเมนูย่อยของหน้าแบรนด์ */
const groupKey = (name: string) => `gmg:${name}`;

export function MyJobSidebar() {
  const collapsed = useSidebarStore((s) => s.collapsed);
  const collapsedMenus = useSidebarStore((s) => s.collapsedMenus);
  const toggleCollapsed = useSidebarStore((s) => s.toggleCollapsed);
  const toggleMenu = useSidebarStore((s) => s.toggleMenu);
  const { data, isPending, isError } = useQuery({
    queryKey: gmKeys.snapshot,
    queryFn: fetchMenuSnapshot,
    staleTime: 5 * 60_000,
  });

  const items = data ?? [];
  // แถบหุบเหลือแต่ไอคอน ไม่มีที่ให้หัวข้อกลุ่ม — แสดงเรียงเดี่ยวทั้งหมดแทนการซ่อนสมาชิกไว้
  const sections = collapsed
    ? items.map((item): Section => ({ kind: "item", item }))
    : toSections(items);

  return (
    <aside className={cn("sidebar", collapsed && "collapsed")} id="sidebar">
      <div className="sidebar-inner">
        {/* หัวข้อของแถบ — เป็นป้ายบอกว่ารายการข้างล่างคืออะไร ไม่ใช่ปุ่มที่กดไปไหนได้
            จึงไม่มีกรอบและไม่มีโลโก้ (โลโก้กลับมาเฉพาะตอนแถบหุบเหลือแต่ไอคอน) */}
        <div className="brand-select sys-head">
          <div className="brand-select-btn" style={{ cursor: "default" }}>
            <span
              className="mini-logo"
              style={{ background: "linear-gradient(135deg,#0866ff,#6a3df5)" }}
            >
              <IconGrid />
            </span>
            <span className="name">ระบบในเครือ</span>
          </div>
        </div>
        <nav className="side-nav sys-nav">
          {/* โหลดไม่ได้ = ไม่มีแถบให้ใช้ ไม่ใช่หน้าพัง — เมนู 3 ขีดยังกดได้ตามปกติ */}
          {isPending || isError || !items.length ? (
            <p className="muted small-note side-nav-note">
              {isPending
                ? "กำลังโหลด…"
                : isError
                  ? "โหลดรายชื่อระบบไม่สำเร็จ"
                  : "ยังไม่มีระบบในเครือ"}
            </p>
          ) : (
            sections.map((s) =>
              s.kind === "item" ? (
                <SysItem key={s.item.url} item={s.item} />
              ) : (
                <div
                  key={`g:${s.name}`}
                  className={cn(
                    "side-item sys-group",
                    !collapsedMenus.includes(groupKey(s.name)) && "expanded",
                  )}
                >
                  <button
                    type="button"
                    className="side-link"
                    onClick={() => toggleMenu(groupKey(s.name))}
                  >
                    <span className="label">{s.name}</span>
                    <span className="side-count">{s.items.length}</span>
                    <IconChevronDown />
                  </button>
                  <div className="submenu">
                    {s.items.map((item) => (
                      <SysItem key={item.url} item={item} />
                    ))}
                  </div>
                </div>
              ),
            )
          )}
        </nav>
      </div>
      <div className="sidebar-footer">
        <button type="button" className="collapse-btn" onClick={toggleCollapsed}>
          <span className="icon">
            <IconCollapse />
          </span>
          <span className="label">Collapse</span>
        </button>
      </div>
    </aside>
  );
}

function SysItem({ item }: { item: GlobalMenuSnapshotItem }) {
  return (
    <div className={cn("side-item", isHere(item.url) && "active")}>
      {/* ปลายทางเป็นคนละโดเมน จึงเป็น <a> ไม่ใช่ <Link> ของ router */}
      <a className="side-link" href={item.url} title={item.description || item.label}>
        <span className="icon">
          <span className="side-sys-logo">{item.label.charAt(0)}</span>
        </span>
        <span className="label">{item.label}</span>
      </a>
    </div>
  );
}
